import {
  CalendarOutlined,
  EditOutlined,
  HistoryOutlined,
  LeftOutlined,
  RightOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Empty,
  InputNumber,
  message,
  Popover,
  Space,
  Spin,
  Timeline,
} from 'antd';
import React, { useState } from 'react';

import { fetchAPI } from '../../utils/api';
import { formatUTCTimeToLocal } from '../../utils/format';
import { safelyParseJSON } from '../Ticketing/SubmittedTicketList';
import { OrderPaymentStatus } from './utils';

const OrderExternalRemittance = (props: {
  value: number;
  orderId: number;
  status: OrderPaymentStatus;
}) => {
  const [value, setValue] = useState({
    real: props.value || 0,
    temp: props.value || 0,
  });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const saveNewValue = async () => {
    setLoading(true);
    try {
      await fetchAPI(`order/specific-update/${props.orderId}`, {
        method: 'PATCH',
        payload: {
          total_external_remittance: value.temp || 0,
        },
      });
      message.success('Success update total remittance');
      setValue(val => ({ ...val, real: val.temp }));
      setEditing(false);
    } catch (error) {
      message.error('Failed update total remittance');
      setValue(val => ({ ...val, temp: val.real }));
    }
    setLoading(false);
  };

  if (editing) {
    return (
      <InputNumber
        value={value.temp}
        placeholder="Input Total"
        onChange={newVal => {
          setValue(val => ({ ...val, temp: newVal }));
        }}
        onBlur={() => saveNewValue()}
        disabled={loading}
      />
    );
  }

  return (
    <>
      <span className="m-label">Total External Remittance (Email Reader)</span>
      {value.real || 0}{' '}
      <EditOutlined
        onClick={() => {
          setEditing(true);
          setValue(val => ({ ...val, temp: val.real }));
        }}
      />{' '}
      <OrderExternalRemittanceHistory orderId={props.orderId} />
    </>
  );
};

const OrderExternalRemittanceHistory = (props: { orderId: number }) => {
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const histories = useQuery(
    [
      'order-external-remittance-history',
      visible,
      page,
      pageSize,
      props.orderId,
    ],
    async () => {
      if (!visible) {
        return {
          items: [],
          page,
          per_page: pageSize,
          total: 0,
          loading: true,
        };
      }

      try {
        const resp = await fetchAPI('user/activity', {
          payload: {
            table_name: 'order',
            value_type: 'update_specific_data',
            row_id: props.orderId,
          },
        });

        return { ...resp, loading: false };
      } catch (error) {
        message.error('Failed get history');
        return {
          items: [],
          page,
          per_page: pageSize,
          total: 0,
          loading: false,
        };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        items: [],
        page,
        per_page: pageSize,
        total: 0,
        loading: true,
      },
    }
  );
  return (
    <Popover
      title="History Order External Remittance"
      trigger={'click'}
      visible={visible}
      onVisibleChange={vis => setVisible(vis)}
      placement="rightTop"
      content={
        <Spin spinning={histories.data.loading || histories.isLoading}>
          {!histories.data.items.length ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Timeline style={{ maxWidth: 230 }}>
              {histories.data.items.map(item => {
                const oldVal = safelyParseJSON(item.old_value)
                  ?.total_external_remittance;
                const newVal = safelyParseJSON(item.new_value)
                  ?.total_external_remittance;
                return (
                  <Timeline.Item dot={<CalendarOutlined />} style={{}}>
                    Change Total from <b>{oldVal || 'nothing'}</b> to{' '}
                    <b>{newVal}</b>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '11px',
                        opacity: '0.7',
                      }}
                    >
                      {formatUTCTimeToLocal(
                        item?.created_at,
                        'DD MMM YYYY HH:mm'
                      )}
                      <div style={{ fontSize: '11px' }}>
                        <UserOutlined /> By {item?.user?.full_name}
                      </div>
                    </span>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          )}

          {(page > 1 || histories.data.total > page * pageSize) && (
            <Space>
              <Button
                icon={<LeftOutlined />}
                onClick={() => setPage(p => p - 1)}
                disabled={page <= 1}
              />
              <Button
                icon={<RightOutlined />}
                onClick={() => setPage(p => p + 1)}
                disabled={histories.data.total <= page * pageSize}
              />
            </Space>
          )}
        </Spin>
      }
    >
      <HistoryOutlined />
    </Popover>
  );
};

export default OrderExternalRemittance;
